import React, { useState, useEffect } from 'react';
import { Download, ArrowLeft, Save } from 'lucide-react';
import ModernTemplate from './templates/ModernTemplate';
import ClassicTemplate from './templates/ClassicTemplate';
import MinimalistTemplate from './templates/MinimalistTemplate';
import CreativeTemplate from './templates/CreativeTemplate';
import ProfessionalTemplate from './templates/ProfessionalTemplate';
import html2pdf from 'html2pdf.js';

import { useSubscription } from '../contexts/SubscriptionContext';
import { Lock } from 'lucide-react';

const TailoredResumePreview = ({ originalResumeData, jobAnalysis, onBack, onSave }) => {
    const { hasFeature, purchaseFeature } = useSubscription();
    const [tailoredData, setTailoredData] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState('modern');
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (originalResumeData && jobAnalysis) {
            // Create a deep copy of the resume data
            const newData = JSON.parse(JSON.stringify(originalResumeData));

            // Apply tailored summary if available
            if (jobAnalysis.tailoredSummary) {
                newData.personal.summary = jobAnalysis.tailoredSummary;
            }

            // Apply skills to add
            if (jobAnalysis.skillsToAdd && Array.isArray(jobAnalysis.skillsToAdd)) {
                // Filter out skills that are already present (case-insensitive check)
                const existingSkills = new Set(newData.skills.map(s => s.toLowerCase()));
                const newSkills = jobAnalysis.skillsToAdd.filter(s => !existingSkills.has(s.toLowerCase()));
                newData.skills = [...newData.skills, ...newSkills];
            }

            // Apply tailored experience
            if (jobAnalysis.tailoredExperience && Array.isArray(jobAnalysis.tailoredExperience)) {
                jobAnalysis.tailoredExperience.forEach(tailoredExp => {
                    const expIndex = newData.experience.findIndex(e => e.id === tailoredExp.id);
                    if (expIndex !== -1) {
                        newData.experience[expIndex].description = tailoredExp.tailoredDescription;
                    }
                });
            }

            setTailoredData(newData);
        }
    }, [originalResumeData, jobAnalysis]);

    const handleDownload = () => {
        if (!tailoredData || !jobAnalysis) return;

        setIsDownloading(true);
        const element = document.getElementById('tailored-resume-preview');
        const firstName = tailoredData.personal.fullName.split(' ')[0] || 'Resume';
        const jobTitle = jobAnalysis.title ? jobAnalysis.title.replace(/\s+/g, '_') : 'Job';
        const filename = `${firstName}_${jobTitle}.pdf`;

        const opt = {
            margin: 0,
            filename: filename,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save().then(() => {
            setIsDownloading(false);
        });
    };

    const handleSaveToDashboard = () => {
        if (!tailoredData || !jobAnalysis) return;

        // Helper: retrieve company from analysis passed from JobAnalyzer? 
        // JobAnalyzer constructs 'job' object but passes 'analysis' object to onTailor?
        // Let's check JobAnalyzer line 574: onTailor(analysis)
        // analysis object (line 166 in aiService) doesn't strictly have 'company' unless I added it?
        // In JobAnalyzer, 'jobTitle' and 'company' are state, but 'analysis' is the JSON from AI.
        // The AI JSON doesn't necessarily have company name unless extracted.
        // However, I can just use "Tailored Resume" + Date?
        // Or "Resume for [Job Title]".
        // Let's just use "Resume for " + (tailoredData.personal.role || "Job").

        // Wait, looking at JobAnalyzer:
        // const job = { title: jobTitle... analysis: analysis ... }
        // But onTailor is called with `analysis`.
        // So `jobAnalysis` prop in TailoredResumePreview is JUST the AI JSON.
        // It doesn't have the user-entered Title/Company from JobAnalyzer inputs unless I included them.
        // Code check: JobAnalyzer.jsx:
        // onTailor(analysis)
        // analysis comes from `JSON.parse`.

        // It seems `jobAnalysis` does NOT have the title/company from the input fields.
        // This is a flaw in the current data passing.
        // However, the AI often extracts it or I can just use a generic name.
        // "Tailored Resume [Date]".

        onSave(tailoredData, `Tailored Resume - ${new Date().toLocaleDateString()}`);
    };

    if (!tailoredData) return <div className="p-8 text-center">Loading tailored resume...</div>;

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col">
            {/* Toolbar */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center sticky top-0 z-50 shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-slate-600 hover:text-slate-800 font-medium"
                    >
                        <ArrowLeft size={20} />
                        Back
                    </button>
                    <div className="h-6 w-px bg-slate-200"></div>
                    <div>
                        <h1 className="font-bold text-slate-800">Tailored Resume Preview</h1>
                        <p className="text-xs text-slate-500">Optimized for job description</p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex bg-slate-100 rounded-lg p-1">
                        <button
                            onClick={() => setSelectedTemplate('modern')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${selectedTemplate === 'modern' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Modern
                        </button>
                        <button
                            onClick={() => setSelectedTemplate('classic')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${selectedTemplate === 'classic' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Classic
                        </button>
                        {/* Premium Templates */}
                        {['minimalist', 'creative', 'professional'].map(template => (
                            <button
                                key={template}
                                onClick={() => {
                                    if (hasFeature('premium_templates')) {
                                        setSelectedTemplate(template);
                                    } else {
                                        purchaseFeature('premium_templates');
                                    }
                                }}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1 ${selectedTemplate === template
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                {template.charAt(0).toUpperCase() + template.slice(1)}
                                {!hasFeature('premium_templates') && <Lock size={12} />}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={handleSaveToDashboard}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors shadow-sm"
                    >
                        <Save size={18} />
                        Save Copy
                    </button>

                    <button
                        onClick={handleDownload}
                        disabled={isDownloading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-70"
                    >
                        {isDownloading ? 'Generating PDF...' : (
                            <>
                                <Download size={18} />
                                Download PDF
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Preview Area */}
            <div className="flex-1 overflow-y-auto p-8 flex justify-center">
                <div className="shadow-2xl">
                    {selectedTemplate === 'modern' && <ModernTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'classic' && <ClassicTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'minimalist' && <MinimalistTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'creative' && <CreativeTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'professional' && <ProfessionalTemplate resumeData={tailoredData} />}
                </div>
                {/* Hidden div for PDF generation to ensure correct ID targeting if needed, 
                    but usually we target the rendered component directly. 
                    However, html2pdf needs the element to be in the DOM. 
                    We can target the wrapper div above. 
                    Let's add an ID to the wrapper of the template.
                */}
            </div>

            {/* Hidden container for PDF generation (if we want a clean render without scaling issues visible on screen) */}
            <div className="absolute left-[-9999px] top-0">
                <div id="tailored-resume-preview">
                    {selectedTemplate === 'modern' && <ModernTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'classic' && <ClassicTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'minimalist' && <MinimalistTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'creative' && <CreativeTemplate resumeData={tailoredData} />}
                    {selectedTemplate === 'professional' && <ProfessionalTemplate resumeData={tailoredData} />}
                </div>
            </div>
        </div>
    );
};

export default TailoredResumePreview;
